import React from "react";
import { Link as InertiaLink, usePage } from "@inertiajs/react";
import {
  Navbar,
  NavbarBrand,
  NavbarMenuToggle,
  NavbarMenuItem,
  NavbarMenu,
  NavbarContent,
  NavbarItem,
  Link,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  DropdownSection,
  Avatar,
  Chip,
  Divider,
} from "@heroui/react";
import { ShiftRunLogo } from "../../components/ShiftRunLogo";

export default function Layout({ children }) {
  const { auth, flash } = usePage().props;
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [showNotice, setShowNotice] = React.useState(false);
  const [showAlert, setShowAlert] = React.useState(false);
  const [isDark, setIsDark] = React.useState(() => {
    if (typeof window !== "undefined") {
      return (
        localStorage.getItem("theme") === "dark" ||
        (!localStorage.getItem("theme") &&
          window.matchMedia("(prefers-color-scheme: dark)").matches)
      );
    }
    return false;
  });

  React.useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [isDark]);

  // Handle flash messages with auto-dismiss
  React.useEffect(() => {
    if (flash.notice) {
      setShowNotice(true);
      const timer = setTimeout(() => setShowNotice(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [flash.notice]);

  React.useEffect(() => {
    if (flash.alert) {
      setShowAlert(true);
      const timer = setTimeout(() => setShowAlert(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [flash.alert]);

  const toggleDarkMode = () => setIsDark(!isDark);

  const menuItems = auth.logged_in
    ? [
        { label: "My tasks", href: "/tasks", icon: "tasks" },
        { label: "Public tasks", href: "/tasks?public=true", icon: "globe" },
        { label: "My runs", href: "/runs", icon: "play" },
        { label: "API keys", href: "/api-keys", icon: "key" },
        { label: "Profile", href: "/profile", icon: "user" },
        { label: "Log out", href: "/logout", method: "delete", icon: "logout" },
      ]
    : [{ label: "Login", href: "/login", icon: "login" }];

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      {/* Subtle background pattern */}
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-50/50 via-transparent to-transparent dark:from-blue-950/20 pointer-events-none" />

      <Navbar
        isBordered={false}
        isMenuOpen={isMenuOpen}
        onMenuOpenChange={setIsMenuOpen}
        classNames={{
          base: "bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-gray-200/50 dark:border-gray-800/50 shadow-sm",
          wrapper: "max-w-7xl px-4 sm:px-6",
        }}
        height="4rem"
      >
        <NavbarContent className="sm:hidden" justify="start">
          <NavbarMenuToggle
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          />
        </NavbarContent>

        <NavbarContent className="sm:hidden pr-3" justify="center">
          <NavbarBrand>
            <InertiaLink href="/" className="flex items-center gap-2.5">
              <div className="bg-gradient-to-br from-amber-500 to-orange-600 p-1.5 rounded-lg shadow-lg shadow-amber-500/25">
                <ShiftRunLogo />
              </div>
              <span className="font-bold text-xl bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                ShiftRun
              </span>
            </InertiaLink>
          </NavbarBrand>
        </NavbarContent>

        <NavbarContent className="hidden sm:flex gap-6" justify="start">
          <NavbarBrand>
            <InertiaLink href="/" className="flex items-center gap-2.5 group">
              <div className="bg-gradient-to-br from-amber-500 to-orange-600 p-1.5 rounded-lg shadow-lg shadow-amber-500/25 group-hover:shadow-amber-500/40 transition-shadow">
                <ShiftRunLogo />
              </div>
              <span className="font-bold text-xl bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                ShiftRun
              </span>
              <Chip
                size="sm"
                variant="flat"
                color="warning"
                classNames={{
                  base: "h-5 bg-amber-100 dark:bg-amber-900/30",
                  content: "text-[10px] font-semibold text-amber-700 dark:text-amber-400",
                }}
              >
                BETA
              </Chip>
            </InertiaLink>
          </NavbarBrand>
        </NavbarContent>

        {auth.logged_in && (
          <NavbarContent className="hidden sm:flex gap-1" justify="center">
            <NavbarItem>
              <Button
                as={InertiaLink}
                href="/tasks"
                variant="light"
                className="text-gray-600 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-800"
                startContent={
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                    />
                  </svg>
                }
              >
                Tasks
              </Button>
            </NavbarItem>
            <NavbarItem>
              <Button
                as={InertiaLink}
                href="/runs"
                variant="light"
                className="text-gray-600 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-800"
                startContent={
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                }
              >
                Runs
              </Button>
            </NavbarItem>
            <NavbarItem>
              <Button
                as={InertiaLink}
                href="/api-keys"
                variant="light"
                className="text-gray-600 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-800"
                startContent={
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                    />
                  </svg>
                }
              >
                API Keys
              </Button>
            </NavbarItem>
          </NavbarContent>
        )}

        <NavbarContent justify="end" className="gap-2">
          <NavbarItem>
            <Button
              isIconOnly
              variant="light"
              onPress={toggleDarkMode}
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
              className="text-gray-600 dark:text-gray-300"
            >
              {isDark ? (
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              ) : (
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                  />
                </svg>
              )}
            </Button>
          </NavbarItem>

          {auth.logged_in ? (
            <NavbarItem>
              <Dropdown placement="bottom-end">
                <DropdownTrigger>
                  <Button
                    variant="light"
                    className="p-0 min-w-0 h-auto bg-transparent data-[hover=true]:bg-transparent"
                  >
                    <Avatar
                      name={getInitials(auth.user.name)}
                      size="sm"
                      classNames={{
                        base: "bg-gradient-to-br from-amber-500 to-orange-600 text-white font-semibold cursor-pointer ring-2 ring-white dark:ring-gray-900 shadow-lg",
                      }}
                    />
                  </Button>
                </DropdownTrigger>
                <DropdownMenu
                  aria-label="User menu"
                  className="w-64"
                  itemClasses={{
                    base: "gap-3",
                  }}
                >
                  <DropdownSection showDivider>
                    <DropdownItem
                      key="profile-header"
                      className="h-14 gap-3 opacity-100"
                      textValue="Profile"
                    >
                      <div className="flex flex-col gap-1">
                        <p className="font-semibold text-gray-900 dark:text-white">
                          {auth.user.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {auth.user.email}
                        </p>
                      </div>
                    </DropdownItem>
                  </DropdownSection>
                  <DropdownSection showDivider>
                    <DropdownItem
                      key="tasks"
                      as={InertiaLink}
                      href="/tasks"
                      startContent={
                        <svg
                          className="w-4 h-4 text-gray-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                          />
                        </svg>
                      }
                    >
                      My tasks
                    </DropdownItem>
                    <DropdownItem
                      key="public-tasks"
                      as={InertiaLink}
                      href="/tasks?public=true"
                      startContent={
                        <svg
                          className="w-4 h-4 text-gray-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      }
                    >
                      Public tasks
                    </DropdownItem>
                    <DropdownItem
                      key="runs"
                      as={InertiaLink}
                      href="/runs"
                      startContent={
                        <svg
                          className="w-4 h-4 text-gray-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13 10V3L4 14h7v7l9-11h-7z"
                          />
                        </svg>
                      }
                    >
                      My runs
                    </DropdownItem>
                    <DropdownItem
                      key="api-keys"
                      as={InertiaLink}
                      href="/api-keys"
                      startContent={
                        <svg
                          className="w-4 h-4 text-gray-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                          />
                        </svg>
                      }
                    >
                      API keys
                    </DropdownItem>
                  </DropdownSection>
                  <DropdownSection>
                    <DropdownItem
                      key="settings"
                      as={InertiaLink}
                      href="/profile"
                      startContent={
                        <svg
                          className="w-4 h-4 text-gray-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                      }
                    >
                      Settings
                    </DropdownItem>
                    <DropdownItem
                      key="logout"
                      as={InertiaLink}
                      href="/logout"
                      method="delete"
                      color="danger"
                      className="text-danger"
                      startContent={
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                          />
                        </svg>
                      }
                    >
                      Log out
                    </DropdownItem>
                  </DropdownSection>
                </DropdownMenu>
              </Dropdown>
            </NavbarItem>
          ) : (
            <NavbarItem>
              <Button
                as={InertiaLink}
                href="/login"
                className="bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 transition-all"
              >
                Get started
              </Button>
            </NavbarItem>
          )}
        </NavbarContent>

        <NavbarMenu className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl pt-6">
          {menuItems.map((item, index) => (
            <NavbarMenuItem key={`${item.label}-${index}`}>
              {item.method === "delete" ? (
                <Link
                  as={InertiaLink}
                  href={item.href}
                  method="delete"
                  className="w-full py-2"
                  color="danger"
                  size="lg"
                >
                  {item.label}
                </Link>
              ) : (
                <Link
                  as={InertiaLink}
                  href={item.href}
                  className="w-full py-2"
                  color="foreground"
                  size="lg"
                >
                  {item.label}
                </Link>
              )}
            </NavbarMenuItem>
          ))}
        </NavbarMenu>
      </Navbar>

      {/* Flash Messages */}
      <div className="fixed top-20 right-4 z-50 max-w-md space-y-2">
        {showNotice && flash.notice && (
          <div className="bg-white dark:bg-gray-800 border-l-4 border-green-500 shadow-xl rounded-lg p-4 flex items-start gap-3 animate-in slide-in-from-right duration-300">
            <div className="flex-shrink-0">
              <svg
                className="w-5 h-5 text-green-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900 dark:text-white">
                Success
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {flash.notice}
              </p>
            </div>
            <button
              onClick={() => setShowNotice(false)}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}
        {showAlert && flash.alert && (
          <div className="bg-white dark:bg-gray-800 border-l-4 border-red-500 shadow-xl rounded-lg p-4 flex items-start gap-3 animate-in slide-in-from-right duration-300">
            <div className="flex-shrink-0">
              <svg
                className="w-5 h-5 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900 dark:text-white">
                Error
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {flash.alert}
              </p>
            </div>
            <button
              onClick={() => setShowAlert(false)}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Main Content */}
      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      {/* Footer */}
      <footer className="relative border-t border-gray-200/50 dark:border-gray-800/50 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="bg-gradient-to-br from-amber-500 to-orange-600 p-1.5 rounded-lg shadow-lg shadow-amber-500/25">
                  <ShiftRunLogo />
                </div>
                <span className="font-bold text-xl bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                  ShiftRun
                </span>
              </div>
              <p className="text-gray-600 dark:text-gray-400 max-w-md">
                Enterprise-grade AI task automation platform. Build, run, and scale
                intelligent workflows with the power of leading AI models.
              </p>
            </div>

            {/* Product */}
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
                Product
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link
                    as={InertiaLink}
                    href="/tasks"
                    className="text-gray-600 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-500 transition-colors"
                  >
                    Tasks
                  </Link>
                </li>
                <li>
                  <Link
                    as={InertiaLink}
                    href="/runs"
                    className="text-gray-600 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-500 transition-colors"
                  >
                    Runs
                  </Link>
                </li>
                <li>
                  <Link
                    as={InertiaLink}
                    href="/api-keys"
                    className="text-gray-600 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-500 transition-colors"
                  >
                    API Keys
                  </Link>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
                Company
              </h3>
              <ul className="space-y-3">
                <li>
                  <span className="text-gray-600 dark:text-gray-400">
                    About
                  </span>
                </li>
                <li>
                  <span className="text-gray-600 dark:text-gray-400">
                    Privacy
                  </span>
                </li>
                <li>
                  <span className="text-gray-600 dark:text-gray-400">
                    Terms
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <Divider className="my-8" />

          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              &copy; {new Date().getFullYear()} ShiftRun. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <Chip
                size="sm"
                variant="flat"
                classNames={{
                  base: "bg-green-100 dark:bg-green-900/30",
                  content: "text-xs font-medium text-green-700 dark:text-green-400",
                }}
              >
                <span className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                  All systems operational
                </span>
              </Chip>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
